
import { defineComponent, inject } from 'vue'
import Checkbox from '@/components/shared/Checkbox.vue'
import CloseSvg from '@/components/svgs/CloseSvg.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import Toaster from '@meforma/vue-toaster'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import ServiceSearchMachine from '@/components/elements/ServiceSearchMachine.vue'
import { QuoteService } from '@/services/quote.service'
import { Service } from '@/@types/responses/service/Service'
import { OrderService } from '@/services/order.service'
import { CreateOrderServiceMessage } from '@/@types/messages/CreateOrderServiceMessage'
import { CreateQuoteServiceMessage } from '@/@types/messages/CreateQuoteServiceMessage'
import { ServiceFieldOption } from '@/@types/responses/service/ServiceFieldOption'
import { DropdownOptionResponse } from '@/@types/responses/common/DropdownOptionResponse'
import CartridgeSearchMachine from '@/components/elements/CartridgeSearchMachine.vue'
import Dropdown from '@/components/shared/Dropdown.vue'
import { ServiceDetails } from '@/@types/responses/service/ServiceDetails'
import { ServiceService } from '@/services/service.service'
import LockingButton from '@/components/shared/LockingButton.vue'
import { ServiceFieldValueDetails } from '@/@types/responses/service/ServiceFieldValueDetails'
import { ServiceFieldValueMessage } from '@/@types/messages/ServiceFieldValueMessage'
import TextArea from '@/components/shared/TextArea.vue'
import TextInput from '@/components/shared/TextInput.vue'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'

export default defineComponent({
  name: 'AddServiceModal',
  components: {
    CartridgeSearchMachine,
    Checkbox,
    CloseSvg,
    Dropdown,
    LoadingPulse,
    LockingButton,
    ServiceSearchMachine,
    TextArea,
    TextInput
  },
  props: {
    orderId: {
      type: Number,
      default: undefined
    },
    quoteId: {
      type: Number,
      default: undefined
    }
  },
  emits: ['close', 'service-added'],
  setup() {
    const orderService = inject(PROVIDER_CONSTANTS.orderService) as OrderService
    const quoteService = inject(PROVIDER_CONSTANTS.quoteService) as QuoteService
    const serviceService = inject(PROVIDER_CONSTANTS.serviceService) as ServiceService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

    return {
      orderService,
      quoteService,
      serviceService,
      toast
    }
  },
  data() {
    let service: ServiceDetails|undefined

    return {
      customerVisible: false,
      note: '',
      saving: false,
      service,
      serviceFieldValues: [] as ServiceFieldValueDetails[]
    }
  },
  computed: {
    isFormValid(): boolean {
      return (this.service !== undefined && !this.serviceFieldValues.some(s => s.value === undefined || s.value.trim() === ''))
    }
  },
  mounted() {
    document.body.addEventListener('keyup', this.onKeyup)
  },
  unmounted() {
    document.body.removeEventListener('keyup', this.onKeyup)
  },
  methods: {
    onCloseClicked(): void {
      this.$emit('close')
    },
    onKeyup(e: KeyboardEvent): void {
      if (e.key === 'Escape' || e.key === 'Esc')
        this.onCloseClicked()
    },
    onSaveClicked(): void {
      if (!this.service) return

      const serviceFieldValues = this.serviceFieldValues.map((serviceFieldValue: ServiceFieldValueDetails) => new ServiceFieldValueMessage(serviceFieldValue.name, serviceFieldValue.serviceFieldId, String(serviceFieldValue.value)))

      if (this.orderId !== undefined) {
        this.saving = true
        const createOrderServiceMessage = new CreateOrderServiceMessage(this.customerVisible, this.note, serviceFieldValues, this.service.id)
        this.orderService.createOrderService(this.orderId, createOrderServiceMessage).then(() => {
          this.$emit('service-added')
          this.toast.success('Service added to order successfully.')
          this.customerVisible = false
          this.note = ''
          this.service = undefined
          this.serviceFieldValues = []
          this.saving = false
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to add the service to the order. Please try again.')
          }
        })
      } else if (this.quoteId !== undefined) {
        this.saving = true
        const createQuoteServiceMessage = new CreateQuoteServiceMessage(this.customerVisible, this.note, serviceFieldValues, this.service.id)
        this.quoteService.createQuoteService(this.quoteId, createQuoteServiceMessage).then(() => {
          this.$emit('service-added')
          this.toast.success('Service added to quote successfully.')
          this.customerVisible = false
          this.note = ''
          this.service = undefined
          this.serviceFieldValues = []
          this.saving = false
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to add the service to the quote. Please try again.')
          }
        })
      }
    },
    onServiceSelected(service: Service): void {
      this.serviceService.getService(service.id).then(serviceModel => {
        const serviceFieldValues = [] as ServiceFieldValueDetails[]
        serviceModel.serviceFields.forEach(serviceField => {
          serviceFieldValues.push(new ServiceFieldValueDetails(serviceField.name, serviceField.id, ''))
        })

        this.serviceFieldValues = serviceFieldValues
        this.service = serviceModel
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the service. Please try again.')
        }
      })
    },
    optionsForDropdown(serviceFieldOptions: ServiceFieldOption[]): DropdownOptionResponse<string>[] {
      const dropdownOptions = [] as DropdownOptionResponse<string>[]
      serviceFieldOptions.forEach(option => {
        dropdownOptions.push(new DropdownOptionResponse(option.id.toString(), option.active, option.value))
      })

      return dropdownOptions
    }
  }
})
